/**
 * Active-Filter-Chips — zeigt jeden gesetzten Filter als Chip mit ✕.
 *
 * Klick auf das X resettet diesen einen Filter; "Alle zurücksetzen" rechts
 * setzt alle gesetzten Filter zurueck.
 */
import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { X } from 'lucide-react';
import { flattenOrgTree } from '../../components/OrgUnitFilter';

const MONTHS = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const BIRTHDAY_WINDOWS = { week: 'Diese Woche', month: 'Dieser Monat', '90d': 'Nächste 90 Tage' };
const STATUS_LABELS = { active: 'Aktiv', inactive: 'Inaktiv' };
const TEMP_LABELS = { yes: 'Befristet', no: 'Unbefristet' };
const ONBOARDING_LABELS = { anwaerter: 'Anwärter', mitarbeiter: 'Mitarbeiter' };

const Chip = ({ label, onRemove, testId }) => (
  <Badge variant="secondary" className="gap-1.5 pl-3 pr-1 py-1 rounded-full" data-testid={testId}>
    <span>{label}</span>
    <button
      type="button"
      onClick={onRemove}
      className="hover:bg-muted/60 rounded-full p-0.5"
      aria-label="Filter entfernen"
    >
      <X className="h-3 w-3" />
    </button>
  </Badge>
);

export const FilterChips = ({ filters, locations, branches, massnahmen, orgTree = [], orgLevels = [], onPatch, onReset }) => {
  const f = filters || {};
  const chips = [];

  const labelOfMassnahme = (id) => massnahmen.find((m) => m.id === id)?.name || id;
  const orgUnitName = (id) => flattenOrgTree(orgTree, orgLevels).find((u) => u.id === id)?.name || id;

  if (f.org_unit && f.org_unit !== 'all') {
    chips.push(
      <Chip key="org" label={`Einheit: ${orgUnitName(f.org_unit)}`}
        onRemove={() => onPatch({ org_unit: 'all' })} testId="chip-org-unit" />
    );
  }
  if (f.az_letter) {
    chips.push(
      <Chip key="az" label={`Buchstabe: ${f.az_letter}`}
        onRemove={() => onPatch({ az_letter: null })} testId="chip-az-letter" />
    );
  }
  if (f.status && f.status !== 'all' && f.status !== 'active') {
    chips.push(
      <Chip key="st" label={`Status: ${STATUS_LABELS[f.status]}`}
        onRemove={() => onPatch({ status: 'active' })} />
    );
  }
  if (f.temporary && f.temporary !== 'all') {
    chips.push(
      <Chip key="tp" label={TEMP_LABELS[f.temporary]}
        onRemove={() => onPatch({ temporary: 'all' })} />
    );
  }
  if (f.onboarding && f.onboarding !== 'all') {
    chips.push(
      <Chip key="onb" label={`Personalstatus: ${ONBOARDING_LABELS[f.onboarding]}`}
        onRemove={() => onPatch({ onboarding: 'all' })} testId="chip-onboarding" />
    );
  }
  if (f.city) {
    chips.push(
      <Chip key="city" label={`Wohnort: ${f.city}`}
        onRemove={() => onPatch({ city: '' })} testId="chip-city" />
    );
  }
  (f.departments || []).forEach((d) => {
    chips.push(
      <Chip key={`dept-${d}`} label={`Maßnahme/Abteilung: ${d}`}
        onRemove={() => onPatch({ departments: f.departments.filter((x) => x !== d) })} />
    );
  });
  (f.positions || []).forEach((p) => {
    chips.push(
      <Chip key={`pos-${p}`} label={`Position: ${p}`}
        onRemove={() => onPatch({ positions: f.positions.filter((x) => x !== p) })} />
    );
  });
  (f.employment_types || []).forEach((t) => {
    chips.push(
      <Chip key={`et-${t}`} label={`${t}`}
        onRemove={() => onPatch({ employment_types: f.employment_types.filter((x) => x !== t) })} />
    );
  });
  (f.massnahmen || []).forEach((m) => {
    chips.push(
      <Chip key={`m-${m}`} label={`Maßnahme: ${labelOfMassnahme(m)}`}
        onRemove={() => onPatch({ massnahmen: f.massnahmen.filter((x) => x !== m) })} />
    );
  });
  (f.qualifications || []).forEach((q) => {
    chips.push(
      <Chip key={`qual-${q}`} label={`Qualifikation: ${q}`}
        onRemove={() => onPatch({ qualifications: f.qualifications.filter((x) => x !== q) })}
        testId={`chip-qualification-${q}`} />
    );
  });
  (f.trainings || []).forEach((t) => {
    chips.push(
      <Chip key={`train-${t}`} label={`Weiterbildung: ${t}`}
        onRemove={() => onPatch({ trainings: f.trainings.filter((x) => x !== t) })}
        testId={`chip-training-${t}`} />
    );
  });
  (f.birth_months || []).forEach((m) => {
    chips.push(
      <Chip key={`bm-${m}`} label={`Geburtsmonat: ${MONTHS[m]}`}
        onRemove={() => onPatch({ birth_months: f.birth_months.filter((x) => x !== m) })} />
    );
  });
  if (f.birthday_window) {
    chips.push(
      <Chip key="bw" label={`Geburtstag: ${BIRTHDAY_WINDOWS[f.birthday_window]}`}
        onRemove={() => onPatch({ birthday_window: null })} />
    );
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex items-start flex-wrap gap-2 pt-1" data-testid="filter-chips">
      {chips}
      <Button
        variant="ghost"
        size="sm"
        onClick={onReset}
        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        data-testid="reset-all-filters"
      >
        Alle zurücksetzen
      </Button>
    </div>
  );
};

export default FilterChips;
